<script lang="ts" setup>
	import StringMethods from '~/utils/character'
	import { getHospitalList, getStatesApi } from '~/apis/hospital-list'
	import { type ProductPlatformKeys } from '~/server/type'
	import type { GroupProductByNameContext } from '~/stores/products/types'
	import type { ProductContext } from '~/types'
	import { storeToRefs } from 'pinia'
	import { useDebounceFn } from '@vueuse/core'

	const { platform = 'business', ...props } = defineProps<{
		groupKey: string
		productCode?: number
		platform?: ProductPlatformKeys
		productGroup?: GroupProductByNameContext | null
		productList?: ProductContext
		useGroupProp?: boolean
		isSendcare?: boolean
		btnClass?: {
			wrapper: any
		}
	}>()

	const { format } = useCurrency()
	const { toLower } = StringMethods()

	const selectedTab = shallowRef('')
	const statesDropdown = ref<{ value: string; label: string }[]>([{ label: 'All States', value: 'all' }])
	const hospitalDropdown = shallowRef([
		{
			name: 'Hospitals',
			id: 'hospital'
		},
		{
			name: 'Eye care',
			id: 'ophthalmology'
		},
		{
			name: 'Dental',
			id: 'dental'
		},
		{
			name: 'Clinic',
			id: 'laboratory centres'
		},
		{
			name: 'Diagnostics',
			id: 'diagnostic centres'
		}
	])
	const filterOptions = reactive({
		location: statesDropdown.value[0],
		search: '',
		type: hospitalDropdown.value[0]
	})
	const loadingTable = shallowRef(false)
	const updatingTable = shallowRef(false)
	const totalHospitalValue = shallowRef(0)
	const hospitalList = ref<HospitalListResponse[]>([])
	const hospitalTemplate = useTemplateRef('hospital-container')
	const hospitalPagination = ref({
		current_page: 1,
		total_pages: 0
	})
	const { getProductsByProductName } = useProductLogic(platform || '')
	const productLabel = computed(() => toLower(props.groupKey ?? ''))
	const productList = computed(() => {
		if (props.groupKey) {
			if (getProductsByProductName.value?.[productLabel.value]) {
				return getProductsByProductName.value[productLabel.value].list || []
			}
			return []
		}
		return []
	})
	const tabList = computed(() =>
		productList.value.map(item => ({
			key: toLower(item.code_name),
			name: String(item?.name ?? '')
		}))
	)
	const highlightedProduct = computed(() => {
		return productList.value.find(item => toLower(item.code_name) === toLower(selectedTab.value)) ?? null
	})

	const getMedicalList = async () => {
		if (!highlightedProduct.value?.id) {
			return null
		}
		const result = await resolve(async () => {
			return await getHospitalList({
				location: filterOptions.location.value === 'all' ? '' : filterOptions.location.value,
				type: filterOptions.type.id,
				search: filterOptions.search,
				product_id: highlightedProduct.value?.id || '',
				page: hospitalPagination.value.current_page,
				user_type: platform
			})
		}, loadingTable).finally(() => {
			updatingTable.value = false
		})

		if (result?.data) {
			const response = result.data
			totalHospitalValue.value = Number(response.total_count ?? 0)
			hospitalPagination.value.total_pages = response.total_pages
			hospitalPagination.value.current_page++
			const list = response.items
			if (list) {
				// Filter Out to make sure no duplicates is found
				if (!hospitalList.value.length) {
					hospitalList.value = list
				} else {
					list.forEach(item => {
						if (hospitalList.value.every(existingItem => existingItem.id !== item.id)) {
							hospitalList.value.push(item)
						}
					})
				}
			}
		}
	}
	const getHospitalCategoryLabel = (id: string) => {
		return hospitalDropdown.value.find(item => item.id === String(id))?.name ?? ''
	}
	const resetFilter = (labels: 'location' | 'search' | '') => {
		switch (labels) {
			case 'location':
				filterOptions.location = statesDropdown.value[0]
				break
			case 'search':
				filterOptions.search = ''
				break
			default:
		}
	}
	const debouncedScroll = useDebounce(async () => {
		const elTarget = hospitalTemplate.value as HTMLDivElement
		const scrollHeight = Math.round(elTarget.clientHeight + elTarget.scrollTop)
		const containerHeight = elTarget.scrollHeight ?? 0

		if (
			scrollHeight >= containerHeight &&
			hospitalPagination.value.current_page < hospitalPagination.value.total_pages + 1
		) {
			updatingTable.value = true
			await getMedicalList()
			elTarget.scroll({ behavior: 'smooth', top: containerHeight - hospitalPagination.value.current_page })
		}
	}, 100)
	const setHospitalList = useDebounceFn(async (recentVal, oldVal) => {
		const isFine = Boolean(
			(recentVal !== oldVal && recentVal && highlightedProduct.value?.id) ||
			(!recentVal && highlightedProduct.value?.id)
		)

		if (isFine && import.meta.client) {
			hospitalPagination.value.current_page = 1
			hospitalList.value = []
			// Wait for hydration before requesting for list
			await nextTick()
			await getMedicalList()
			if (hospitalTemplate.value) {
				hospitalTemplate.value.addEventListener('scroll', debouncedScroll)
			}
		}
	}, 100)

	const { isMobile } = useMobile()
	const tableColumns = computed(() => {
		if (isMobile.value) {
			return [
				{
					key: 'name',
					label: '',
					class: 'opacity-0 '
				}
			]
		}
		return [
			{
				key: 'name',
				label: '',
				class: 'opacity-0 '
			},
			{ key: 'type', label: '', class: 'opacity-0 lg:min-w-[200px] ' },
			{
				key: 'location',
				label: '',
				class: 'opacity-0'
			}
		]
	})

	onUnmounted(() => {
		if (hospitalTemplate.value) {
			hospitalTemplate.value.removeEventListener('scroll', debouncedScroll)
		}
	})
	onMounted(async () => {
		const states = await resolve(() => getStatesApi())
		if (states) {
			statesDropdown.value = [{ label: 'All States', value: 'all' }, ...states]
		}
	})

	// Watch for Product name real-time changes
	watch(
		() => props.groupKey,
		() => {
			if (import.meta.client && props?.groupKey) {
				if (props.productCode) {
					selectedTab.value = String(props.productCode)
				} else {
					selectedTab.value = String(
						productList.value.find(item => item.group_id === props.groupKey)?.code_name ?? ''
					)
				}
			}
		},
		{ immediate: true }
	)

	const colors = computed(() => {
		if (props.isSendcare) {
			return {
				bg: 'bg-[#1A473F]',
				activeColor: 'bg-[#092A25]',
				text: 'text-[#00E0B8]',
				inactiveBtn: {
					color: 'text-white',
					default: ''
				}
			}
		}
		return {
			bg: 'bg-health-500',
			text: 'text-white',
			inactiveBtn: {
				color: 'text-health-500',
				default: 'border-health-500 border'
			}
		}
	})

	watch([selectedTab, highlightedProduct], setHospitalList, { immediate: true })

	watch(
		() => filterOptions,
		() => {
			hospitalPagination.value.current_page = 1
			hospitalList.value = []
			getMedicalList()
		},
		{ deep: true }
	)
</script>

<template>
	<div class="w-full" data-allow-mismatch id="hospital-list">
		<client-only>
			<tools-tab
				:tabs="tabList"
				v-model:active-tab="selectedTab"
				:ui="{
					line: {
						border: '',
						default: `${colors.activeColor || colors.bg || ''} rounded-[40px] mb-[5px]`,
						height: 'h-[calc(100%-10px)]'
					},
					tab: {
						default: 'relative rounded-[40px]',
						zIndex: 'z-20',
						body: { border: '', default: isSendcare ? 'bg-[#1A473F] rounded-[100px] px-2' : '' },
						wrapper: {
							display: 'flex justify-center items-center',
							default: ''
						},
						active: {
							color: colors.text
						},
						padding: 'p-6',
						height: '',
						inactive: {
							color: colors.inactiveBtn.color,
							default: colors.inactiveBtn.default
						}
					}
				}"
			/>
		</client-only>

		<div :class="colors.bg" class="w-full py-10 md:px-8 px-2 md:p-10 mt-8 mx-auto">
			<client-only>
				<div
					class="md:min-h-[72px] w-full bg-white md:mb-14 mb-5 rounded md:py-5 md:px-5 px-3 flex items-center"
				>
					<ui-search-field
						v-model:value="filterOptions.search"
						:use-custom-class="false"
						class="w-full"
						input-class="sm:pr-5 pr-1"
					/>
					<ul class="sm:inline-flex hidden">
						<li>
							<u-select-menu
								v-model="filterOptions.type"
								searchable-placeholder="Search"
								option-attribute="name"
								class="border-l-2 w-36 cursor-pointer"
								placeholder="Hospitals"
								:options="hospitalDropdown"
								size="lg"
								variant="none"
							>
								<template #empty> No Hospital Category </template>
							</u-select-menu>
						</li>
						<li>
							<u-select-menu
								v-model="filterOptions.location"
								searchable-placeholder="Search"
								option-attribute="label"
								class="w-36 border-l-2"
								placeholder="All States"
								:options="statesDropdown"
								variant="none"
								size="lg"
							>
								<template #empty> States not available</template>
							</u-select-menu>
						</li>
					</ul>
				</div>

				<ul class="flex-wrap bg-white inline-flex sm:hidden">
					<li>
						<u-select-menu
							v-model="filterOptions.type"
							searchable-placeholder="Search"
							option-attribute="name"
							class="border-x md:px-5 cursor-pointer"
							placeholder="Hospitals"
							:options="hospitalDropdown"
							variant="none"
							size="xl"
						>
							<template #empty> No Hospital Category </template>
						</u-select-menu>
					</li>
					<li>
						<u-select-menu
							v-model="filterOptions.location"
							searchable-placeholder="Search"
							option-attribute="label"
							placeholder="All States"
							:options="statesDropdown"
							variant="none"
							size="xl"
							select-class="md:px-5 rounded-sm focus:border-base-primary bg-white"
							:ui="{ option: { padding: 'px-1.5 py-2' }, container: 'z-20 group min-w-[200px]' }"
						>
							<template #empty> States not available</template>
						</u-select-menu>
					</li>
				</ul>
			</client-only>

			<div id="value displays" class="flex items-center flex-wrap gap-3 pt-5 mb-10">
				<client-only>
					<div class="inline-flex bg-white p-[6px_20px] rounded-[20px] text-health-900 font-medium text-base">
						{{ format(totalHospitalValue) }} {{ filterOptions.type?.name ?? 'Hospital' }}
					</div>
				</client-only>

				<client-only>
					<ui-pill
						:label="String(highlightedProduct?.name ?? '--').replace('care', 'Care')"
						:ui="{ padding: 'p-[6px_14px]', size: 'xs:text-base', base: 'bg-health-100 capitalize' }"
					/>
				</client-only>

				<ui-pill
					v-if="filterOptions.search"
					:ui="{ base: 'bg-health-100 capitalize', padding: 'p-[6px_14px]', size: 'xs:text-base' }"
				>
					<span>{{ filterOptions.search }}</span>
					<button type="button" name="remove" class="rotate-45" @click="resetFilter('search')">
						<tools-icon dir="line" icon="crosshair" :height="16" :width="16" />
					</button>
				</ui-pill>

				<ui-pill
					v-if="filterOptions.location?.value && filterOptions.location?.value !== 'all'"
					:ui="{ base: 'bg-health-100 capitalize', padding: 'p-[6px_14px]', size: 'xs:text-base' }"
				>
					<span>{{ filterOptions.location.value }}</span>
					<button type="button" name="remove" @click="resetFilter('location')" class="rotate-45">
						<tools-icon dir="line" icon="crosshair" :height="16" :width="16" />
					</button>
				</ui-pill>
			</div>

			<ui-spacer :ui="{ marginBlock: 'sm:mb-16 mb-8 relative' }">
				<div
					class="absolute custom-transition-2 left-0 z-10 bottom-0 flexed justify-center pointer-events-none bg-gradient-to-tr w-full h-full"
					:class="[
						{
							'opacity-0': !updatingTable && !loadingTable
						},
						$style.gradient
					]"
					data-allow-mismatch
				>
					<span class="text-white capitalize tracking-wide font-semibold md:text-xl text-lg">
						{{ updatingTable ? 'Updating' : 'Loading' }} {{ filterOptions.type?.name ?? '' }} list ...
					</span>
				</div>

				<div
					class="max-h-[510px] w-full bg-white overflow-y-auto overflow-hidden md:px-10 p-5 relative"
					ref="hospital-container"
				>
					<lazy-client-only>
						<u-table
							:loading="loadingTable"
							:loading-state="{ icon: 'i-heroicons-arrow-path-20-solid', label: 'Loading...' }"
							:progress="{ color: 'primary', animation: 'carousel' }"
							class="w-full"
							:columns="tableColumns"
							:rows="hospitalList"
							:ui="{
								divide: '',
								td: {
									base: 'text-balance',
									padding: 'pb-5',
									font: 'font-medium ',
									color: 'text-gray-500 dark:text-gray-400'
								}
							}"
						>
							<template #name-data="{ row }">
								<div class="text-wrap max-w-[500px]">
									<p class="font-medium text-base-500 text-sm lg:hidden">{{ row?.location ?? '' }}</p>
									<strong
										class="text-base capitalize font-semibold md:leading-[24px] text-gray-600 leading-[14.4px]"
									>
										{{ row?.name ?? '' }}
									</strong>
									<p class="text-xs">({{ row?.address ?? '' }})</p>
								</div>
							</template>
							<template #type-data="{ row }">
								<span class="text-xs">{{ getHospitalCategoryLabel(row?.type) }}</span>
							</template>
						</u-table>
					</lazy-client-only>
				</div>
			</ui-spacer>
		</div>
	</div>
</template>

<style module>
	.gradient {
		background-color: rgba(0, 0, 0, 0.5);
		backdrop-filter: blur(2px);
	}
</style>
